import { createApiRef, IdentityApi } from '@backstage/core-plugin-api';
import {
  MembersData,
  SecretVaultServiceApi,
  UserMapData,
  Response,
  Namespace,
  NamespaceMetadataResponse,
} from './secret-vault.service.type';
import { RestClientApi, toGithubRepositoryRequest } from '@runway/devkit-common';
import type { GithubRepository } from '@runway/devkit-common';
import { jwtDecode } from 'jwt-decode';

export const SecretVaultServiceApiRef = createApiRef<SecretVaultServiceApi>({
  id: 'plugin.secret-vault.service',
});

export class SecretVaultService implements SecretVaultServiceApi {
  constructor(
    private backendUrl: string,
    private identityApi: IdentityApi,
    private restClient: RestClientApi,
  ) {}

  private getSSOToken = async (): Promise<string> => {
    const { token } = await this.identityApi.getCredentials();
    const { aaSso } = jwtDecode<{ aaSso: string }>(token || '');
    return aaSso;
  };

  async createNamespace(
    namespaceName: string,
    adminAdGroup: string,
    readerAdGroup: string,
    appShortname: string,
    squadName: string,
    squadId: number,
    k8s_namespace: string,
    hostClusters: string[],
    githubRepos: GithubRepository[],
    githubOrg: string,
    adSecretEngine?: boolean,
    approleAuth?: boolean,
  ): Promise<Response> {
    try {
      const token = await this.getSSOToken();
      const response = await this.restClient.post(
        `${this.backendUrl}/namespaces`,
        {
          namespace: namespaceName,
          ad_group_admin: adminAdGroup,
          ad_group_reader: readerAdGroup,
          custom_metadata: {
            app_shortname: appShortname,
            squad_name: squadName,
            squad_id: squadId,
          },
          k8s_namespace_name: k8s_namespace,
          cluster_names: hostClusters,
          ...toGithubRepositoryRequest(githubOrg, githubRepos),
          ad_secret_engine: adSecretEngine,
          approle_auth: approleAuth,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );
      return response;
    } catch (error: any) {
      return error;
    }
  }

  async getNameSpacesFromShortName(shortName: string): Promise<Namespace[]> {
    try {
      const token = await this.getSSOToken();
      const response = await this.restClient.get(`${this.backendUrl}/plugin-authentication`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        params: { appshortname: shortName },
      });
      return response.data;
    } catch (error: unknown) {
      if (this.restClient.isRestError(error)) {
        const detail = error.response?.data?.detail;
        if (typeof detail === 'string' && detail.length > 0) {
          throw new Error(detail, { cause: error });
        }
      }

      if (error instanceof Error) {
        throw error;
      }

      throw new Error('Failed to fetch vault namespaces', { cause: error });
    }
  }

  async editNamespaceForGithubRepo({
    namespace,
    github_repos,
    github_org,
  }: {
    namespace: string;
    github_repos: GithubRepository[];
    github_org: string;
  }): Promise<Response> {
    try {
      const token = await this.getSSOToken();
      const response = await this.restClient.post(
        `${this.backendUrl}/github`,
        {
          namespace,
          ...toGithubRepositoryRequest(github_org, github_repos),
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );
      return response;
    } catch (error: any) {
      return error;
    }
  }

  async editNamespaceForKubernetes({
    namespace,
    cluster_names,
    k8s_namespace_name,
  }: {
    namespace: string;
    cluster_names?: string[];
    k8s_namespace_name?: string;
  }): Promise<Response> {
    try {
      const token = await this.getSSOToken();
      const response = await this.restClient.post(
        `${this.backendUrl}/kubernetes`,
        {
          namespace,
          cluster_names,
          k8s_namespace_name,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );
      return response;
    } catch (error: any) {
      return error;
    }
  }

  async getUsermapData({ aaId }: { aaId: string }): Promise<UserMapData[]> {
    try {
      const token = await this.getSSOToken();
      const response = await this.restClient.get(`${this.backendUrl}/ad_user_details`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        params: { aaid: aaId },
      });
      return response.data;
    } catch (error) {
      throw new Error(`Failed to get user map data for user: ${aaId}`, { cause: error });
    }
  }

  async getManageGroupsData({ groupName }: { groupName: string }): Promise<Record<string, string>[]> {
    try {
      const token = await this.getSSOToken();
      const response = await this.restClient.get(`${this.backendUrl}/ad_members/${groupName}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      return response.data;
    } catch (error: any) {
      return error;
    }
  }

  async addManageGroupsData({
    members,
    groupName,
  }: {
    members: MembersData[];
    groupName: string;
  }): Promise<{ data: string; status: number }> {
    try {
      const token = await this.getSSOToken();
      const response = await this.restClient.post(
        `${this.backendUrl}/ad_members`,
        {
          members: members.map((member: MembersData) => ({
            friendly_name: member.Name,
          })),
          groups: [groupName],
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );
      return response;
    } catch (error: any) {
      return error;
    }
  }

  async deleteManageGroupsData({
    members,
    groupName,
  }: {
    members: MembersData[];
    groupName: string;
  }): Promise<{ data: string; status: number }> {
    try {
      const token = await this.getSSOToken();
      const response = await this.restClient.post(
        `${this.backendUrl}/delete_members`,
        {
          members: members.map((member: MembersData) => ({
            friendly_name: member.Name,
          })),
          groups: [groupName],
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );
      return response;
    } catch (error: any) {
      return error;
    }
  }

  async createPostgresSecretsEngine({
    namespace,
    envLabel,
    dbServer,
    dbName,
    adminUsername,
    adminPassword,
  }: {
    namespace: string;
    envLabel: string;
    dbServer: string;
    dbName: string;
    adminUsername: string;
    adminPassword: string;
  }): Promise<Response> {
    try {
      const token = await this.getSSOToken();
      const response = await this.restClient.post(
        `${this.backendUrl}/postgres`,
        {
          namespace: namespace,
          label: envLabel,
          db_server: dbServer,
          db_name: dbName,
          admin_username: adminUsername,
          admin_password: adminPassword,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );
      return response;
    } catch (error: any) {
      return error;
    }
  }

  async getNamespaceMetadata(namespace: string): Promise<NamespaceMetadataResponse> {
    const token = await this.getSSOToken();
    const response = await this.restClient.get(`${this.backendUrl}/metadata`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
      params: {
        namespace,
      },
    });
    return response.data;
  }
}
